import { Parent } from '@/models/Parent'
import { Teacher } from '@/models/Teacher'
import { ParentRepository } from '@/repository/ParentRepository'
import { StudentRepository } from '@/repository/StudentRepository'
import { TeacherRepository } from '@/repository/TeacherRepository'
import { RequestRepository } from '@/repository/RequestRepository'

class AcceptRequestService {
  constructor(client, user) {
    if (!(client)) {
      throw new Error(`'client' is required.`)
    }
    this.service = AcceptRequestServiceFactory.create(client, user)
  }

  async execute(requestId) {
    return await this.service.execute(requestId)
  }
}

class AcceptRequestServiceFactory {
  static create(client, user) {
    if (user instanceof Parent) {
      return new ParentAcceptRequestService(client, user)
    } else if (user instanceof Teacher) {
      return new TeacherAcceptRequestService(client, user)
    } else {
      throw new Error(`'user' must be an instance of either [object Parent] or [object Teacher]. Actual: ${typeof user}`)
    }
  }
}

class ParentAcceptRequestService {
  constructor(client, parent) {
    this.parentRepo = new ParentRepository(client)
    this.studentRepo = new StudentRepository(client)
    this.teacherRepo = new TeacherRepository(client)
    this.requestRepo = new RequestRepository(client)
    this.parent = parent
  }

  async execute(requestId) {
    const request = await this._acceptRequest(requestId)
    const teacher = await this._updateTeacher(request)
    return await this.parentRepo.update(this.parent.updateStudent(await this._updateStudent(request)).registerTeacherId(teacher.getId()))
  }

  async _updateStudent(request) {
    const student = this.parent.findStudent(request.studentId)
    return await this.studentRepo.update(student.registerTeacherId(request.senderId))
  }

  async _updateTeacher(request) {
    const teacher = await this.teacherRepo.find(request.senderId)
    return await this.teacherRepo.update(teacher.registerStudentId(request.studentId))
  }

  async _acceptRequest(requestId) {
    const request = await this.requestRepo.find(requestId)
    return await this.requestRepo.update(request.toAccepted())
  }

}

class TeacherAcceptRequestService {
  constructor(client, teacher) {
    this.parentRepo = new ParentRepository(client)
    this.studentRepo = new StudentRepository(client)
    this.teacherRepo = new TeacherRepository(client)
    this.requestRepo = new RequestRepository(client)
    this.teacher = teacher
  }

  async execute(requestId) {
    const request = await this._acceptRequest(requestId)
    const parent = this._updateParent(request)
    return await this._updateTeacher(request)
  }

  async _updateParent(request) {
    const parent = await this.parentRepo.find(request.senderId)
    const student = await this._updateStudent(parent.findStudent(request.studentId))
    return await this.parentRepo.update(parent.updateStudent(student).registerTeacherId(this.teacher.getId()))
  }

  async _updateStudent(student) {
    return await this.studentRepo.update(student.registerTeacherId(this.teacher.getId()))
  }

  async _updateTeacher(request) {
    return await this.teacherRepo.update(this.teacher.registerStudentId(request.studentId))
  }

  async _acceptRequest(requestId) {
    const request = await this.requestRepo.find(requestId)
    return await this.requestRepo.update(request.toAccepted())
  }
}

export { AcceptRequestService }